/**
 * Select — headless primitive wrapping Radix Select.
 *
 * Radix's Select owns the WAI-ARIA Listbox pattern used for native-like
 * dropdowns: combobox semantics, typeahead, arrow-key navigation,
 * keyboard-only operation, collision detection, portal, and focus
 * return (ARCHITECTURE §6). We wrap thinly:
 *   - Permission gating on <Root> (denied → null + permission:denied
 *     audit; no useful visible-but-inert state)
 *   - Audit on open / close (`select:open` / `select:close`) and on
 *     value change (`select:change`)
 *   - `data-oshon-primitive="select-*"` styling hooks
 *
 * "use client" mandatory — Radix uses hooks + DOM APIs internally.
 */

'use client';

import * as RadixSelect from '@radix-ui/react-select';
import { forwardRef } from 'react';
import type {
  ComponentPropsWithoutRef,
  ElementRef,
  ReactNode,
} from 'react';
import {
  evaluatePermission,
  resolvePermissions,
  usePermissions,
  useAudit,
  type PermissionContext,
} from '../context.js';
import { CANONICAL_ACTIONS } from '../actions.js';

// ────────────────────────────────────────────────────────────────────────────
// Root

export interface SelectRootProps
  extends Omit<RadixSelect.SelectProps, 'onOpenChange' | 'onValueChange'> {
  /**
   * Fires whenever the select's open state changes. Primitive emits
   * `select:open` / `select:close` audit before the callback runs.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Fires whenever the user picks a value. Primitive emits
   * `select:change` audit with `{ value }` details before the callback
   * runs.
   */
  onValueChange?: (value: string) => void;
  /**
   * Per-instance permission override. Merges over the ambient
   * `PermissionContext`. Denied → primitive returns null (a select has
   * no useful visible-but-inert state — the components layer can render
   * a static readout for that case) and emits `permission:denied`.
   */
  permissions?: Partial<PermissionContext>;
  /**
   * Resource name for `permissions.can('view', resource, attrs)`.
   * Default `'select'`. Override for e.g. `resource="field:status"`.
   */
  resource?: string;
  /** Attribute bag for attribute-based access control. */
  permissionAttrs?: Record<string, unknown>;
  children: ReactNode;
}

function Root({
  children,
  value,
  defaultValue,
  onValueChange,
  open,
  defaultOpen,
  onOpenChange,
  permissions,
  resource = 'select',
  permissionAttrs,
  ...rest
}: SelectRootProps) {
  const context = usePermissions();
  const effective = resolvePermissions(permissions, context);
  const emit = useAudit();

  const { granted, denyReason } = evaluatePermission(
    effective,
    'view',
    resource,
    permissionAttrs,
  );

  if (!granted) {
    emit({
      component: 'select',
      action: CANONICAL_ACTIONS.PERMISSION_DENIED,
      permissionResult: 'denied',
      ...(denyReason ? { denyReason } : {}),
      details: { attemptedAction: 'view', resource },
    });
    return null;
  }

  const handleOpenChange = (next: boolean) => {
    emit({
      component: 'select',
      action: next ? CANONICAL_ACTIONS.SELECT_OPEN : CANONICAL_ACTIONS.SELECT_CLOSE,
      permissionResult: 'granted',
    });
    onOpenChange?.(next);
  };

  const handleValueChange = (next: string) => {
    emit({
      component: 'select',
      action: CANONICAL_ACTIONS.SELECT_CHANGE,
      permissionResult: 'granted',
      details: { value: next },
    });
    onValueChange?.(next);
  };

  return (
    <RadixSelect.Root
      value={value}
      defaultValue={defaultValue}
      onValueChange={handleValueChange}
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={handleOpenChange}
      {...rest}
    >
      {children}
    </RadixSelect.Root>
  );
}
Root.displayName = 'Select.Root';

// ────────────────────────────────────────────────────────────────────────────
// Trigger

export type SelectTriggerProps = ComponentPropsWithoutRef<typeof RadixSelect.Trigger>;

const Trigger = forwardRef<
  ElementRef<typeof RadixSelect.Trigger>,
  SelectTriggerProps
>(function SelectTrigger(props, ref) {
  return (
    <RadixSelect.Trigger
      ref={ref}
      data-oshon-primitive="select-trigger"
      {...props}
    />
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Value

export type SelectValueProps = ComponentPropsWithoutRef<typeof RadixSelect.Value>;

/**
 * Reads the currently-selected item's text content. Radix renders a
 * `<span>` with `data-placeholder` when no value is set — the
 * components layer styles both states.
 */
const Value = forwardRef<
  ElementRef<typeof RadixSelect.Value>,
  SelectValueProps
>(function SelectValue(props, ref) {
  return <RadixSelect.Value ref={ref} {...props} />;
});

// ────────────────────────────────────────────────────────────────────────────
// Icon

export type SelectIconProps = ComponentPropsWithoutRef<typeof RadixSelect.Icon>;

const Icon = forwardRef<
  ElementRef<typeof RadixSelect.Icon>,
  SelectIconProps
>(function SelectIcon(props, ref) {
  return (
    <RadixSelect.Icon
      ref={ref}
      data-oshon-primitive="select-icon"
      {...props}
    />
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Portal

export type SelectPortalProps = RadixSelect.SelectPortalProps;

function Portal(props: SelectPortalProps) {
  return <RadixSelect.Portal {...props} />;
}
Portal.displayName = 'Select.Portal';

// ────────────────────────────────────────────────────────────────────────────
// Content

export type SelectContentProps = ComponentPropsWithoutRef<typeof RadixSelect.Content>;

const Content = forwardRef<
  ElementRef<typeof RadixSelect.Content>,
  SelectContentProps
>(function SelectContent(props, ref) {
  return (
    <RadixSelect.Content
      ref={ref}
      data-oshon-primitive="select-content"
      {...props}
    />
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Viewport

export type SelectViewportProps = ComponentPropsWithoutRef<typeof RadixSelect.Viewport>;

const Viewport = forwardRef<
  ElementRef<typeof RadixSelect.Viewport>,
  SelectViewportProps
>(function SelectViewport(props, ref) {
  return (
    <RadixSelect.Viewport
      ref={ref}
      data-oshon-primitive="select-viewport"
      {...props}
    />
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Item

export type SelectItemProps = ComponentPropsWithoutRef<typeof RadixSelect.Item>;

const Item = forwardRef<
  ElementRef<typeof RadixSelect.Item>,
  SelectItemProps
>(function SelectItem(props, ref) {
  return (
    <RadixSelect.Item
      ref={ref}
      data-oshon-primitive="select-item"
      {...props}
    />
  );
});

// ────────────────────────────────────────────────────────────────────────────
// ItemText / ItemIndicator / Separator / Group / Label

export type SelectItemTextProps = ComponentPropsWithoutRef<typeof RadixSelect.ItemText>;

const ItemText = forwardRef<
  ElementRef<typeof RadixSelect.ItemText>,
  SelectItemTextProps
>(function SelectItemText(props, ref) {
  return <RadixSelect.ItemText ref={ref} {...props} />;
});

export type SelectItemIndicatorProps = ComponentPropsWithoutRef<typeof RadixSelect.ItemIndicator>;

const ItemIndicator = forwardRef<
  ElementRef<typeof RadixSelect.ItemIndicator>,
  SelectItemIndicatorProps
>(function SelectItemIndicator(props, ref) {
  return (
    <RadixSelect.ItemIndicator
      ref={ref}
      data-oshon-primitive="select-item-indicator"
      {...props}
    />
  );
});

export type SelectSeparatorProps = ComponentPropsWithoutRef<typeof RadixSelect.Separator>;

const Separator = forwardRef<
  ElementRef<typeof RadixSelect.Separator>,
  SelectSeparatorProps
>(function SelectSeparator(props, ref) {
  return (
    <RadixSelect.Separator
      ref={ref}
      data-oshon-primitive="select-separator"
      {...props}
    />
  );
});

export type SelectGroupProps = ComponentPropsWithoutRef<typeof RadixSelect.Group>;

const Group = forwardRef<
  ElementRef<typeof RadixSelect.Group>,
  SelectGroupProps
>(function SelectGroup(props, ref) {
  return <RadixSelect.Group ref={ref} {...props} />;
});

export type SelectLabelProps = ComponentPropsWithoutRef<typeof RadixSelect.Label>;

const Label = forwardRef<
  ElementRef<typeof RadixSelect.Label>,
  SelectLabelProps
>(function SelectLabel(props, ref) {
  return (
    <RadixSelect.Label
      ref={ref}
      data-oshon-primitive="select-label"
      {...props}
    />
  );
});

// ────────────────────────────────────────────────────────────────────────────
// Compound export

/**
 * Compound Select primitive. Use as:
 *
 *   <Select.Root defaultValue="open">
 *     <Select.Trigger aria-label="Status">
 *       <Select.Value placeholder="Select…" />
 *       <Select.Icon>▾</Select.Icon>
 *     </Select.Trigger>
 *     <Select.Portal>
 *       <Select.Content>
 *         <Select.Viewport>
 *           <Select.Item value="open">
 *             <Select.ItemText>Open</Select.ItemText>
 *             <Select.ItemIndicator>✓</Select.ItemIndicator>
 *           </Select.Item>
 *           <Select.Item value="closed">
 *             <Select.ItemText>Closed</Select.ItemText>
 *           </Select.Item>
 *         </Select.Viewport>
 *       </Select.Content>
 *     </Select.Portal>
 *   </Select.Root>
 */
export const Select = {
  Root,
  Trigger,
  Value,
  Icon,
  Portal,
  Content,
  Viewport,
  Item,
  ItemText,
  ItemIndicator,
  Separator,
  Group,
  Label,
};

export type SelectProps = SelectRootProps;
